import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  RemainingExecutionsAndTenantNameQuery,
  RemainingExecutionsAndTenantNameQueryVariables,
} from 'molbio/aa-sequences/alphafold/__generated/RemainingExecutionsAndTenantNameQuery';
import {REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export function getTooltipText(tenantName: string): string {
  const timeUnit = BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC ? 'year' : 'month';
  // Get the datetime info for the next moment when this user's AlphaFold runs will reset.
  const date = moment.tz('America/Los_Angeles').add(1, timeUnit).startOf(timeUnit);

  const tenantQualifier = BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC ? '' : ` across ${tenantName}`;
  return `You have a total of ${
    BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT
  } requests per ${timeUnit}${tenantQualifier}. Requests will reset back to ${
    BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT
  } on ${date.format('LT z')} at the start of every calendar ${timeUnit}.`;
}

export const useGetRemainingExecutionsAndTenantName = (): {
  remainingExecutions: number;
  tenantName: string;
} => {
  const queryResult = useBenchlingQuery<
    RemainingExecutionsAndTenantNameQuery,
    RemainingExecutionsAndTenantNameQueryVariables
  >(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY, {
    variables: {},
  });
  return queryResult.type === QueryResultType.SUCCESS
    ? {
        remainingExecutions: queryResult.data.remainingAlphafoldExecutions,
        tenantName: queryResult.data.currentTenant.displayName,
      }
    : {remainingExecutions: 0, tenantName: ''};
};

const ModelHubExecutionCountNotice = ({inBanner = true}: {inBanner?: boolean}): JSX.Element => {
  const {remainingExecutions, tenantName} = useGetRemainingExecutionsAndTenantName();
  const quota = BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT;

  if (!BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return <></>;
  }
  if (inBanner) {
    return (
      <Tooltip text={getTooltipText(tenantName)}>
        <InlineBanner
          text={`${remainingExecutions} of ${quota} requests remaining`}
          bannerType={remainingExecutions <= 0 ? InlineBannerType.WARNING : InlineBannerType.INFO}
        />
      </Tooltip>
    );
  } else {
    return (
      <div className={css(styles.optionButtonsLayout)}>
        <div>
          <Tooltip text={getTooltipText(tenantName)}>
            <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
          </Tooltip>
          <span className={css(styles.infoText)}>
            {remainingExecutions} of {quota} requests remaining
          </span>
        </div>
      </div>
    );
  }
};

export default ModelHubExecutionCountNotice;

const styles = StyleSheet.create({
  infoIcon: {
    color: GRAY_DARKER,
  },
  infoText: {
    ...FONT_PROXIMA_13,
    marginLeft: '8px',
  },
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '35px',
  },
});
